import { useStore } from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { AspectRatio } from '@src/types.ts/video';
import { LogManager } from '@src/utility/Logger';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import { VideoTemplatePreviewCard } from '../sidepanel/VideoTemplateSettings/VideoTemplatePreviewCard';
import {
  getCurrentOverrideConfigurationKey,
  mergeConfiguration,
} from '../sidepanel/VideoTemplateSettings/videoTemplateUtils';
import Accordion from './Accordion';

import type {
  VideoTemplate,
  VideoTemplateConfig,
  VideoTemplateConfigured,
} from '../sidepanel/VideoTemplateSettings/types';
import type { DatoColor } from '@src/types.ts/story';

const logger = LogManager.getLogger('BrandKit:VideoTemplates');

/** Extract human-readable composition labels from a template config. */
function getCompositionLabels(config: VideoTemplateConfig | null): string[] {
  if (!config) return [];
  const labels: string[] = [];
  if (config.logoConfiguration?.enabled) labels.push('Logo');
  if (config.introCardConfiguration?.enabled) labels.push('Intro Card');
  if (config.endCardConfiguration?.enabled) labels.push('End Card');
  if (config.captionsConfiguration?.enabled) labels.push('Captions');
  if (config.namecardConfiguration?.enabled) labels.push('Namecard');
  return labels;
}

/** Format composition labels as a comma-separated list. */
function formatCompositionLabels(labels: string[]): string {
  return labels.join(', ');
}

/** Extract caption-related colors (hex) from a template config. */
function getCaptionColors(config: VideoTemplateConfig | null): string[] {
  const captionsConfig = config?.captionsConfiguration?.captionsConfig;
  if (!captionsConfig) return [];

  const toHex = (color: DatoColor | null | undefined): string | null => {
    if (!color) return null;
    return color.hex || null;
  };

  const colors: string[] = [];
  const fill = toHex(captionsConfig.fillColor);
  const bg = toHex(captionsConfig.backgroundColor);
  const transcript = toHex(captionsConfig.transcriptColor);
  const stroke = toHex(captionsConfig.strokeColor);

  if (fill) colors.push(fill);
  if (bg) colors.push(bg);
  if (transcript) colors.push(transcript);
  if (stroke) colors.push(stroke);

  return colors;
}

const BrandKitVideoTemplates = observer(() => {
  const videoCreator = useVideoCreatorStore();
  const { datoClientStore } = useStore();

  const [allVideoTemplates, setAllVideoTemplates] = useState<VideoTemplate[]>(
    [],
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);

  const templateIds = useMemo(
    () =>
      videoCreator.organization?.customerType?.videoTemplates?.map(t => t.id) ||
      [],
    [videoCreator.organization?.customerType?.videoTemplates],
  );

  const excludeFromGlobalTemplate =
    videoCreator.organization?.customerType?.excludeFromGlobalTemplate ?? false;

  // Fetch full templates from repository
  useEffect(() => {
    let cancelled = false;
    const fetchTemplates = async () => {
      setIsLoading(true);
      try {
        const templates =
          await datoClientStore.videoTemplateRepository.getVideoTemplates(
            templateIds,
            { excludeGlobalPresets: excludeFromGlobalTemplate },
          );
        if (!cancelled) {
          setAllVideoTemplates(templates);
        }
      } catch (error) {
        logger.error('Failed to fetch video templates', error);
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    fetchTemplates();
    return () => {
      cancelled = true;
    };
  }, [datoClientStore, templateIds, excludeFromGlobalTemplate]);

  // Merge configurations for 16:9 preview (BrandKit has no video context)
  const videoTemplatesConfigured = useMemo(() => {
    const overrideKey = getCurrentOverrideConfigurationKey(AspectRatio.AR_16_9);
    return allVideoTemplates.map(
      (vt): VideoTemplateConfigured => ({
        id: vt.id,
        label: vt.label,
        internalName: vt.internalName,
        configuration: mergeConfiguration(
          vt.commonConfiguration,
          vt[overrideKey],
          AspectRatio.AR_16_9,
        ),
      }),
    );
  }, [allVideoTemplates]);

  // Selection state — null means "No Video Template"
  const [selectedId, setSelectedId] = useState<string | null>(
    videoCreator.organization?.defaultVideoTemplate?.id ?? null,
  );

  // Sync when organization loads asynchronously
  useEffect(() => {
    const orgDefault = videoCreator.organization?.defaultVideoTemplate?.id;
    setSelectedId(orgDefault ?? null);
  }, [videoCreator.organization?.defaultVideoTemplate?.id]);

  const handleSelect = useCallback(
    async (templateId: string | null) => {
      setSelectedId(templateId);
      setIsSaving(true);
      setIsSaved(false);

      try {
        const organization = videoCreator.organization;
        if (!organization) return;

        organization.defaultVideoTemplate = templateId
          ? { id: templateId }
          : null;
        await datoClientStore.albumRepository?.update(organization);
        setIsSaved(true);
      } catch (error) {
        logger.error('Failed to save default video template', error);
      } finally {
        setIsSaving(false);
      }
    },
    [videoCreator, datoClientStore],
  );

  return (
    <Accordion
      id="video-templates"
      title={`Video Templates (${videoTemplatesConfigured.length})`}
      isSaving={isSaving}
      isSaved={isSaved}
    >
      <Subtitle>
        The selected Video Template will be automatically applied to all newly
        generated clips in Clip Production
      </Subtitle>
      <TemplateContainer>
        {isLoading ? (
          <LoadingText>Loading templates...</LoadingText>
        ) : (
          <TemplateGrid>
            <CardWrapper
              isSelected={selectedId === null}
              onClick={() => handleSelect(null)}
            >
              <NoTemplateCard>No Video Template</NoTemplateCard>
            </CardWrapper>

            {videoTemplatesConfigured.map(template => {
              const compositionLabels = getCompositionLabels(
                template.configuration,
              );
              const captionColors = getCaptionColors(template.configuration);

              return (
                <CardWrapper
                  key={template.id}
                  isSelected={selectedId === template.id}
                  onClick={() => handleSelect(template.id)}
                >
                  <CardTitle>{template.label}</CardTitle>
                  <VideoTemplatePreviewCard videoTemplate={template} />
                  <CardFooter>
                    <CompositionLabels>
                      {compositionLabels.length > 0
                        ? formatCompositionLabels(compositionLabels)
                        : 'No Graphics'}
                    </CompositionLabels>
                    {captionColors.length > 0 && (
                      <ColorCircles>
                        {captionColors.map((color, i) => (
                          <ColorCircle key={i} color={color} />
                        ))}
                      </ColorCircles>
                    )}
                  </CardFooter>
                </CardWrapper>
              );
            })}
          </TemplateGrid>
        )}
      </TemplateContainer>
    </Accordion>
  );
});

const Subtitle = styled.p`
  color: var(--text-muted);
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  line-height: 1.5;
  margin: 0 0 16px;
`;

const TemplateContainer = styled.div`
  background: var(--surface-elevated);
  border-radius: 8px;
  padding: 24px;
`;

const TemplateGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 12px;
`;

const CardWrapper = styled.div<{ isSelected: boolean }>`
  display: flex;
  flex-direction: column;
  cursor: pointer;
  border-radius: 8px;
  border: 1px solid
    ${({ isSelected }) => (isSelected ? 'var(--primary)' : 'var(--border)')};
  background: var(--background);
  padding: 12px;
  gap: 8px;
  transition: border-color var(--transition-fast) ease;

  &:hover {
    border-color: var(--primary);
  }
`;

const NoTemplateCard = styled.div`
  width: 100%;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-secondary);
  font-size: var(--text-xs);
  text-align: center;
`;

const CardTitle = styled.div`
  color: var(--foreground);
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  line-height: 16px;
  max-width: 100%;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`;

const CardFooter = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const CompositionLabels = styled.div`
  color: rgba(255, 255, 255, 0.4);
  font-family: Inter, sans-serif;
  font-size: 11px;
  font-weight: 400;
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ColorCircles = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const ColorCircle = styled.div<{ color: string }>`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: ${p => p.color};
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const LoadingText = styled.div`
  color: var(--text-muted);
  font-size: var(--text-xs);
  text-align: center;
  padding: 24px;
`;

export default BrandKitVideoTemplates;
